import { RefObject, useLayoutEffect } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  anticipatePinForStackSection,
  pinEndScrollDistance,
} from "./stack-pin-anticipate";

export const STACK_PIN_SKIP_ATTR = "data-stack-pin";
export const STACK_PIN_SKIP_VALUE = "skip";
export const STACK_PIN_PRECISE_ATTR = "data-stack-pin-precise";

const AWARDS_SECTION_PIN_CLASS = "awards-section--pinned";

export function useMainPageStackPin(
  mainPageRef: RefObject<HTMLElement | null>,
  awardsSectionRef: RefObject<HTMLElement | null>,
) {
  useLayoutEffect(() => {
    const mainPage = mainPageRef.current;
    if (!mainPage) {
      return;
    }

    const sections = Array.from(mainPage.children).filter(
      (node): node is HTMLElement =>
        node instanceof HTMLElement && node.tagName === "SECTION",
    );

    if (sections.length <= 2) {
      return;
    }

    const triggers: ScrollTrigger[] = [];
    const awardsEl = awardsSectionRef.current;

    sections.forEach((section, index) => {
      if (index === sections.length - 1) {
        return;
      }
      if (section.getAttribute(STACK_PIN_SKIP_ATTR) === STACK_PIN_SKIP_VALUE) {
        return;
      }

      const isAwardsSection = awardsEl !== null && section === awardsEl;
      const pinPrecise = section.hasAttribute(STACK_PIN_PRECISE_ATTR);

      triggers.push(
        ScrollTrigger.create({
          trigger: section,
          scroller: document.documentElement,
          start: "top top",
          end: pinEndScrollDistance(section),
          pin: true,
          pinSpacing: false,
          pinType: "fixed",
          anticipatePin: anticipatePinForStackSection(pinPrecise, index),
          invalidateOnRefresh: true,
          ...(isAwardsSection && awardsEl
            ? {
                onToggle(self) {
                  awardsEl.classList.toggle(
                    AWARDS_SECTION_PIN_CLASS,
                    self.isActive,
                  );
                },
              }
            : {}),
        }),
      );
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        ScrollTrigger.refresh();
      });
    });

    return () => {
      awardsEl?.classList.remove(AWARDS_SECTION_PIN_CLASS);
      triggers.forEach((trigger) => trigger.kill());
    };
  }, [awardsSectionRef, mainPageRef]);
}
